import {
  ChangeDetectionStrategy,
  Component,
  input,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TreatmentPlanFacade } from '@principle-theorem/ng-clinical-charting/store';
import {
  ITreatmentPlan,
  ITreatmentPlanProposal,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  HISTORY_DATE_FORMAT,
  isWithRef,
  multiMap,
  snapshot,
  type DocumentReference,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TreatmentPlanningBloc } from '../../treatment-planning-bloc';
import {
  ITreatmentPlanListDialogData,
  TreatmentPlanListDialogComponent,
} from '../treatment-plan-list-dialog/treatment-plan-list-dialog.component';
import { DialogPresets } from '@principle-theorem/ng-shared';

@Component({
  selector: 'pr-treatment-plan-comparison',
  templateUrl: './treatment-plan-comparison.component.html',
  styleUrls: ['./treatment-plan-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreatmentPlanComparisonComponent {
  private _dialog = inject(MatDialog);
  private _treatmentPlanFacade = inject(TreatmentPlanFacade);

  treatmentPlanning = input.required<TreatmentPlanningBloc>();
  patient$ = input.required<Observable<WithRef<IPatient>>>();

  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly emptyProposal: ITreatmentPlanProposal = {
    forks: [],
    treatments: [],
    multiTreatments: [],
  };

  async changeSelectedPlan(): Promise<void> {
    const currentPlanRef = await snapshot(
      this.treatmentPlanning().selectedPlan$.pipe(map((plan) => plan?.ref))
    );
    const selectedPlan = await this._openPlanListDialog(currentPlanRef);

    if (selectedPlan && isWithRef(selectedPlan)) {
      this._treatmentPlanFacade.selectTreatmentPlan(selectedPlan.ref);
    }
  }

  async changeComparisonPlan(): Promise<void> {
    const currentPlanRef = await snapshot(
      this.treatmentPlanning().comparisonPlan$.pipe(map((plan) => plan?.ref))
    );
    const selectedPlan = await this._openPlanListDialog(currentPlanRef);

    if (selectedPlan && isWithRef(selectedPlan)) {
      this.treatmentPlanning().setComparisonPlan(selectedPlan);
    }
  }

  private async _openPlanListDialog(
    currentPlanRef?: DocumentReference<ITreatmentPlan>
  ): Promise<WithRef<ITreatmentPlan> | ITreatmentPlan | undefined> {
    const data = await this._getPlanListDialogData(currentPlanRef);
    return this._dialog
      .open<
        TreatmentPlanListDialogComponent,
        ITreatmentPlanListDialogData,
        WithRef<ITreatmentPlan> | ITreatmentPlan
      >(
        TreatmentPlanListDialogComponent,
        DialogPresets.large({
          data,
        })
      )
      .afterClosed()
      .toPromise();
  }

  private async _getPlanListDialogData(
    currentPlanRef?: DocumentReference<ITreatmentPlan>
  ): Promise<ITreatmentPlanListDialogData> {
    const patient = await snapshot(this.patient$());
    const planPairs = await snapshot(
      this.treatmentPlanning().patientPlans$.pipe(
        multiMap((plan) => ({ plan, patient }))
      )
    );
    const todaysPlan = await snapshot(
      this.treatmentPlanning().appointmentPlan$.pipe(map((plan) => plan?.ref))
    );
    return {
      planPairs,
      todaysPlan,
      selectedPlan: currentPlanRef,
    };
  }
}
